import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, Users, Building, Activity, Briefcase, FileText, CheckCircle } from "lucide-react";

// Images
const HERO_BG = "https://static.wixstatic.com/media/e92216_6c2741b76d3242b7bacef411cbb4bcdd~mv2.jpg"; 
const CHIEF_IMG = "https://images.unsplash.com/photo-1544985220-54ee036d3845?q=80&w=2669&auto=format&fit=crop"; 

const Home = () => {
  return (
    <div className="flex flex-col w-full">
      {/* 1. Hero Section */}
      <section className="relative h-[90vh] w-full bg-black flex flex-col justify-center">
        {/* BG Image */}
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} />
        <div className="absolute inset-0 bg-black/40" /> 
        
        <div className="relative container mx-auto px-4 z-10 pt-20">
           <div className="max-w-4xl mx-auto text-center">
             <div className="text-[#D97706] font-serif font-bold tracking-widest mb-2 text-xl md:text-2xl drop-shadow-md">
               ᒥᔪᑌᐦ ᑲ ᐃᐧᓴᒥᐦᐟ ᐊᐄᐧᔭᐠ
             </div>
             
             <div className="text-white font-sans font-bold tracking-[0.3em] text-lg md:text-xl mb-0 drop-shadow-md">
               WELCOME TO
             </div>
             
             <h1 className="text-[#FFC72C] font-heading font-bold text-7xl md:text-9xl tracking-tighter leading-none mb-6 drop-shadow-lg">
               FROG LAKE
             </h1>
             
             <p className="font-script text-white text-3xl md:text-5xl tracking-wide opacity-90 drop-shadow-md transform -rotate-2">
               "We are determined to do good things and committed to achieving results"
             </p>

             <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-[#D97706] hover:bg-[#B45309] text-white font-heading font-bold uppercase tracking-widest px-10 py-6 rounded-none text-sm">
                  <Link to="/about">About Our Nation</Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="bg-transparent text-white border-2 border-white hover:bg-white hover:text-black font-heading font-bold uppercase tracking-widest px-10 py-6 rounded-none text-sm">
                  <Link to="/residents">Community Info</Link>
                </Button>
             </div>
           </div>
        </div>

        {/* Floating Cards - Links to Pages */}
        <div className="absolute bottom-0 w-full translate-y-1/2 z-20 hidden lg:block">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-4 gap-6">
              {[
                { title: "Business & Growth", icon: Briefcase, color: "text-blue-600", link: "/business" },
                { title: "Chief & Council", icon: Users, color: "text-yellow-500", link: "/governance" },
                { title: "Programs & Activities", icon: Activity, color: "text-green-600", link: "/residents#recreation" },
                { title: "News & Updates", icon: Calendar, color: "text-red-500", link: "/news" },
              ].map((item, i) => (
                <Link key={i} to={item.link}>
                  <div className="bg-white p-8 shadow-xl flex flex-col items-center text-center group hover:-translate-y-2 transition-transform duration-300 cursor-pointer border-t-4 border-transparent hover:border-[#D97706] h-full">
                    <item.icon className={`h-10 w-10 mb-4 ${item.color}`} />
                    <h3 className="font-heading font-bold text-lg uppercase text-zinc-800">{item.title}</h3>
                    <div className="h-1 w-8 bg-gray-200 mt-4 group-hover:bg-[#D97706] transition-colors" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Spacer for floating cards */}
      <div className="h-32 hidden lg:block bg-zinc-50" />

      {/* 2. Economic Advantage (Business Snippet) */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1 relative h-[400px]">
             <img src="https://static.wixstatic.com/media/e92216_73f3f16545264ed99b9c8d0bfe188264~mv2.jpg/v1/fill/w_1904,h_470,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/e92216_73f3f16545264ed99b9c8d0bfe188264~mv2.jpg" alt="Business" className="absolute inset-0 w-full h-full object-cover rounded shadow-lg" />
             <div className="absolute -top-6 -left-6 w-32 h-32 bg-[#D97706] hidden md:block -z-10" />
          </div>
          <div className="order-1 lg:order-2">
            <h4 className="text-[#D97706] font-heading font-bold uppercase tracking-widest mb-4 text-sm">Economic Development</h4>
            <h2 className="text-4xl font-heading font-bold mb-6 text-black uppercase">
              Investing in Frog Lake
            </h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Take advantage of streamlined processes and great benefits to get your business up and running. 
              We offer the lowest corporate tax rate in Canada (10%) and no provincial sales tax.
            </p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-[#D97706]" />
                <span className="font-bold text-zinc-800">Same-day permit approvals</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-[#D97706]" />
                <span className="font-bold text-zinc-800">Dedicated economic development committee</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-[#D97706]" />
                <span className="font-bold text-zinc-800">Investment per capita 2x Canadian average</span>
              </li>
            </ul>
            <Button asChild variant="outline" className="rounded-none border-zinc-900 text-zinc-900 hover:bg-zinc-900 hover:text-white uppercase font-bold tracking-widest">
              <Link to="/business">View Opportunities</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* 3. Community Life (Residents Snippet) */}
      <section className="py-24 bg-zinc-50 border-t border-zinc-200">
        <div className="container mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h4 className="text-[#D97706] font-heading font-bold uppercase tracking-widest mb-4 text-sm">Community Life</h4>
            <h2 className="text-4xl font-heading font-bold mb-6 text-black uppercase">
              A Vibrant Place to Live
            </h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Our community is rich in culture and provides top-tier facilities for residents. 
              From the Chief Napeweaw Comprehensive School to the new Recreation Centre featuring an ice arena 
              and fitness gym, Frog Lake is building for the future.
            </p>
            <div className="grid grid-cols-2 gap-4 mb-8">
               <div className="bg-white p-4 border rounded shadow-sm text-center">
                 <Building className="h-8 w-8 mx-auto text-[#D97706] mb-2" />
                 <h4 className="font-bold uppercase text-sm">Health Centre</h4>
               </div>
               <div className="bg-white p-4 border rounded shadow-sm text-center">
                 <Activity className="h-8 w-8 mx-auto text-[#D97706] mb-2" />
                 <h4 className="font-bold uppercase text-sm">Rec Centre</h4>
               </div>
            </div>
            <Button asChild className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-none uppercase font-bold tracking-widest px-8">
              <Link to="/residents">Resident Services</Link>
            </Button>
          </div>
          <div className="relative h-[400px]">
             <img src={CHIEF_IMG} alt="Community" className="absolute inset-0 w-full h-full object-cover rounded shadow-lg" />
             <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-zinc-800 hidden md:block -z-10" />
          </div>
        </div>
      </section>

      {/* 4. Strategic Priorities */}
      <section className="py-24 bg-white border-t border-zinc-200">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-heading font-bold uppercase mb-4">Strategic Priorities</h2>
            <div className="h-1 w-20 bg-[#D97706] mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: "PARTNERSHIPS", icon: Users, desc: "Collaborating for mutual growth", link: "/partners" },
              { title: "FISCAL RESPONSIBILITY", icon: Building, desc: "Accountable resource management", link: "/governance" },
              { title: "SAFE COMMUNITY", icon: Activity, desc: "Health and wellness for all", link: "/residents" },
              { title: "BUSINESS GROWTH", icon: Briefcase, desc: "Economic development initiatives", link: "/business" },
            ].map((item, i) => (
              <Link key={i} to={item.link} className="group">
                <div className="bg-zinc-50 p-10 shadow-sm hover:shadow-xl transition-all duration-300 border-b-4 border-transparent hover:border-[#D97706] h-full text-center">
                  <div className="h-16 w-16 bg-white rounded-full flex items-center justify-center mb-6 mx-auto group-hover:bg-[#D97706] group-hover:text-white transition-colors shadow-sm">
                    <item.icon className="h-8 w-8 text-gray-400 group-hover:text-white" />
                  </div>
                  <h3 className="font-heading font-bold text-xl mb-3 text-black uppercase tracking-wide">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Latest News */}
      <section className="py-24 bg-zinc-50 border-t border-zinc-200">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-end mb-12 border-b border-gray-200 pb-4">
            <div>
              <h4 className="text-[#D97706] font-bold uppercase tracking-widest mb-2 text-xs">Community Updates</h4>
              <h2 className="text-3xl font-heading font-bold uppercase">Latest News</h2>
            </div>
            <Link to="/news" className="text-sm font-bold uppercase tracking-widest flex items-center gap-2 hover:text-[#D97706] transition-colors">
              All News <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="group cursor-pointer">
                <div className="relative overflow-hidden mb-6 aspect-[4/3] bg-gray-200">
                  <img 
                    src={`https://images.unsplash.com/photo-1533038590840-1cde6e668a91?q=80&w=2574&auto=format&fit=crop`} 
                    alt="News" 
                    className="object-cover w-full h-full opacity-90 group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors" />
                  <div className="absolute top-4 left-4 bg-white text-black text-xs font-bold px-3 py-1 uppercase tracking-wider">
                    June {10 + i}
                  </div>
                </div>
                <h3 className="font-heading font-bold text-xl mb-3 group-hover:text-[#D97706] transition-colors leading-tight uppercase">
                  Community Announcement: Housing Project Update Phase {i}
                </h3>
                <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                  The council is pleased to announce the next phase of the sustainable housing development...
                </p>
                <span className="text-xs font-bold uppercase tracking-widest border-b border-black pb-1 group-hover:border-[#D97706] group-hover:text-[#D97706] transition-colors">
                  Read Article
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
